import React, { useState } from "react";
import BuyOnStoreButton from "web/react/components/buy-on-store-button/buy-on-store-button";
import { Conditional } from "web/react/components/conditional";
import LeadLink from "web/react/components/lead-link/lead-link";
import { Promo } from "web/react/components/promo-offer/pdp-promo";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { HStack } from "web/react/emo/h-stack";
import { Heading } from "web/react/emo/heading";
import ListItem from "web/react/emo/list-item/list-item";
import { Pill } from "web/react/emo/pill";
import { styles } from "web/react/emo/product-comparison-table";
import { Price } from "web/react/emo/product-price";
import { Retailer } from "web/react/emo/retailer";
import { PromoTag, Sticker, StickerProps } from "web/react/emo/sticker";
import { Text } from "web/react/emo/text";
import { View } from "web/react/emo/view";
import {
    getDefaultSizeList,
    getPromotion,
    getShippingInformation,
    getSizePrice,
} from "web/react/pages/product/in-stock/in-stock-product-page//utils";
import { gettext } from "web/script/modules/django-i18n";
import { BuyOptionSerializer, SizeSerializer } from "web/types/serializers";

export function TableRow({
    buyOption,
    stickerType,
    termsUrl,
}: {
    buyOption: BuyOptionSerializer;
    stickerType?: StickerProps["name"];
    termsUrl: string;
}): JSX.Element {
    const defaultSize = buyOption.sizes[buyOption.default_size] as SizeSerializer;
    const price = getSizePrice(defaultSize);
    const shipping = getShippingInformation(buyOption, defaultSize);
    const sizeList = getDefaultSizeList(buyOption.sizes);
    const pillList = sizeList.slice(0, 4);
    const promotion = getPromotion(buyOption, termsUrl);
    const [showPromo, setShowPromo] = useState(true);

    const priceArea = (
        <View className={styles.priceArea}>
            <Price {...price} size={"lg"} />
            {stickerType && (
                <span className={styles.sticker["desktop"]}>
                    <Sticker name={stickerType} />{" "}
                </span>
            )}
            {!!promotion && (
                <div className={styles.promoCta["desktop"]}>
                    <PromoTag
                        onStickerClick={() => {
                            setShowPromo(!showPromo);
                        }}
                        hide={showPromo}
                    />
                </div>
            )}
        </View>
    );

    const retailerArea = (
        <View className={styles.retailerArea}>
            <span>
                <LeadLink
                    href={defaultSize.lead_url}
                    rel="noreferrer"
                    reason="retailer-link"
                    openInNewTab
                >
                    <span className={styles.retailerName["noFrom"]}>
                        <Heading textStyle={"subhead"} color={"primary"}>
                            {buyOption.retailer_name}
                        </Heading>
                    </span>
                    <span className={styles.retailerName["withFrom"]}>
                        <Retailer
                            retailerName={buyOption.retailer_name}
                            fromSpanStyle={"capitalize"}
                        />
                    </span>
                </LeadLink>
            </span>
            {stickerType && (
                <span className={styles.sticker["tablet"]}>
                    <Sticker name={stickerType} />{" "}
                </span>
            )}
            {!!promotion && (
                <div className={styles.promoCta["tablet"]}>
                    <PromoTag
                        onStickerClick={() => {
                            setShowPromo(!showPromo);
                        }}
                        hide={showPromo}
                    />
                </div>
            )}
        </View>
    );

    const shippingInfoArea = (
        <View className={styles.shippingInfoArea}>
            <ul>
                {shipping.map((shippingInfo, i) => (
                    <ListItem key={i} variant={"primary"} size="xs" flexWrapperHeight>
                        <SVGIcon
                            name={shippingInfo.isFree ? "tick" : "plus"}
                            className={styles.tickIcon}
                            overrideStyle
                        />
                        <Text
                            textStyle={"body-2"}
                            color={"secondary"}
                            className={styles.shippingInfo}
                            as="span"
                        >
                            {shippingInfo.info}
                        </Text>
                    </ListItem>
                ))}
            </ul>
        </View>
    );

    const sizePillsArea = (
        <View className={styles.sizePillsArea}>
            <div>
                <HStack spacing="xxs" wrap>
                    {pillList?.map((size) => (
                        <Pill key={size}>{size}</Pill>
                    ))}
                    <Conditional check={sizeList.length > 3}>
                        <Pill>
                            {/* "+ {count} More" */}
                            {gettext("product.buy_area.sizes_more_count.label", {
                                count: sizeList.length - 3,
                            })}
                        </Pill>
                    </Conditional>
                </HStack>
            </div>
        </View>
    );

    const buttonArea = (
        <View className={styles.buttonArea}>
            {!!promotion && (
                <div className={styles.promoCta["mobile"]}>
                    <PromoTag
                        onStickerClick={() => {
                            setShowPromo(!showPromo);
                        }}
                        hide={showPromo}
                    />
                </div>
            )}
            <BuyOnStoreButton
                productId={buyOption.product_id}
                leadUrl={defaultSize.lead_url}
                analyticsEventLabel={"comparison_option"}
                leadLabel={gettext("product.buy_area.buy_now")}
                contextType={"comparison_option"}
                reason={"buy-on-store-alternative-option"}
                retailerSlug={buyOption.retailer_slug}
            />
        </View>
    );

    return (
        <View className={styles.comparisonRowWrapper}>
            <View
                className={styles.comparisonRow["option"]}
                paddingY={"sm"}
                paddingX={"md"}
                marginBottom={{ sm: "md", md: "none" }}
            >
                {priceArea}
                {retailerArea}
                {shippingInfoArea}
                {sizePillsArea}
                {buttonArea}
            </View>
            {showPromo && (
                <div className={styles.promotionArea}>
                    <Promo promotion={promotion} variant="row" />
                </div>
            )}
        </View>
    );
}
